// src/auth/PermissionsContext.jsx
import React, { createContext, useMemo, useContext } from "react";
import { useAuthContext } from "./useAuthContext";

const PermissionsContext = createContext(null);

const toSet = (arr) => new Set(Array.isArray(arr) ? arr.filter(Boolean) : []);

export function PermissionsProvider({ children }) {
    const { user, isAuthenticated, isInitialized } = useAuthContext();

    // تعتمد على permissions_names القادمة من /auth/me (في الذاكرة فقط)
    const permissionsSet = useMemo(() => toSet(user?.permissions_names), [user]);

    const api = useMemo(() => {
        const has = (perm) => permissionsSet.has(perm);
        const hasAny = (perms = []) => perms.some((p) => permissionsSet.has(p));
        const hasAll = (perms = []) => perms.every((p) => permissionsSet.has(p));
        const hasPrefix = (prefix) => {
            for (const p of permissionsSet) if (p.startsWith(prefix)) return true;
            return false;
        };

        // helper عام: can("countries", "create") => "countries_create"
        const can = (resource, action) => has(`${resource}_${action}`);

        // ✅ استخراج الأدوار من user.roles (array)
        const userRoles = user?.roles || [];
        const userRolesNames = user?.roles_names || [];

        return {
            isInitialized,
            isAuthenticated,
            permissionsSet,
            has,
            hasAny,
            hasAll,
            hasPrefix,
            can,
            user,
            userRoles, // ✅ array من الأدوار
            userRolesNames, // ✅ array من أسماء الأدوار
        };
    }, [permissionsSet, isAuthenticated, isInitialized, user]);

    return <PermissionsContext.Provider value={api}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
    const ctx = useContext(PermissionsContext);
    if (!ctx) throw new Error("usePermissions must be used within PermissionsProvider");
    return ctx;
}
